/**
 * Thin fetch wrapper for the public status API.
 *
 * No credentials, no cookies — these endpoints are public read-only
 * (and the webhook subscribe form is unauth too).
 *
 * Base URL resolution: this project builds ONE static bundle that
 * serves BOTH status.collectandtrack.lol and status.collectandtrack.xyz
 * (same Vercel deployment, both domains attached), so a single
 * build-time `VITE_API_BASE_URL` can't be correct for both — it'd
 * point one of the two mirrors at the wrong backend. Instead, resolve
 * api.<mirror> from the page's own hostname at runtime, same approach
 * as client/src/api/client.ts. Falls back to `/api` for local dev
 * (Vite proxies that to localhost:3000 — see vite.config.ts) or an
 * explicit `VITE_API_BASE_URL` override for anything else.
 */

const MIRROR_API_ORIGINS: Record<string, string> = {
  "collectandtrack.lol": "https://api.collectandtrack.lol",
  "collectandtrack.xyz": "https://api.collectandtrack.xyz",
};

function resolveApiBaseUrl(): string {
  if (typeof window !== "undefined") {
    const root = window.location.hostname.split(".").slice(-2).join(".");
    const mirrored = MIRROR_API_ORIGINS[root];
    if (mirrored) return mirrored;
  }
  return import.meta.env.VITE_API_BASE_URL ?? "/api";
}

const API_BASE_URL = resolveApiBaseUrl();

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiException extends Error {
  constructor(
    public readonly status: number,
    public readonly apiError: ApiError,
  ) {
    super(apiError.message);
    this.name = "ApiException";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = options;
  
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...rest,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    reportDowntimeIfAny();
  } catch (err: any) {
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      recordDowntime();
    }
    throw new ApiException(503, { code: "network_error", message: "Impossible de joindre le serveur", details: err.message });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      /* non-JSON response */
    }
  }

  if (!res.ok) {
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      recordDowntime();
    }
    const apiError =
      (data as { error?: ApiError } | null)?.error ?? {
        code: "unknown_error",
        message: res.statusText || "Request failed",
        details: data ?? text,
      };
    throw new ApiException(res.status, apiError);
  }

  return data as T;
}

function recordDowntime() {
  if (typeof localStorage !== "undefined" && !localStorage.getItem("dr_downtime_start")) {
    localStorage.setItem("dr_downtime_start", Date.now().toString());
  }
}

function reportDowntimeIfAny() {
  if (typeof localStorage === "undefined") return;
  const startStr = localStorage.getItem("dr_downtime_start");
  if (startStr) {
    const start = parseInt(startStr, 10);
    const durationSeconds = Math.round((Date.now() - start) / 1000);
    localStorage.removeItem("dr_downtime_start");
    
    if (durationSeconds > 0) {
      fetch(`${API_BASE_URL}/status/offline-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ durationSeconds }),
      }).catch(() => {});
    }
  }
}

